/**
 * Educator module (v1) - content aligned with Educators_Module_LMS_Content_Mapping.md
 * UI flow mirrors Building Workplace Bridges in StudyModule.js.
 */

export const EDUCATOR_CONTENT = {
  id: 'educator-module',
  title: 'Educator Module',
  subtitle: 'Breaking Barriers: Systemic Patterns in STEMM Pathways for Educators',
  description:
    'For faculty, academic administrators, and STEMM program leads who shape how qualification converts into opportunity.',
  courseDescriptionLong:
    'This module is designed for educators - including faculty, academic administrators, and STEMM program leads at post-secondary institutions - who shape access to opportunity through mentorship, selection, evaluation, and everyday norms.',
  learningGoals: [
    'Recognize how barriers are produced through academic structures and decision rules - not individual student deficits.',
    'Identify educator-specific leverage points at key transition stages - especially at the Bachelor’s level and the university → work handoff.',
    'Distinguish mentorship from sponsorship and evaluate whether their current practices generate real advocacy and visibility.',
  ],
  sections: [
    {
      id: 'overview',
      title: 'Module Purpose & Learning Goals',
      content:
        '<h3>How to Use this Module</h3><p>This module is designed for <strong>faculty, academic administrators, and STEMM program leads</strong> at post-secondary institutions.</p><p>Meta-reflexivity is introduced as a practice you will carry across the activities that follow.</p>',
    },
    {
      id: 'anika-story',
      title: "Sara's Case Study & Pause & Reflect",
      content:
        '<h3>Case Study: Sara</h3><p>Sara represents <strong>patterned dynamics</strong> - not an exceptional case. Notice small moments: who explains unwritten rules, who interprets silence, and the consequences.</p>',
    },
    {
      id: 'barriers',
      title: 'The Five Barriers: At-A-Glance',
      content:
        '<p>These patterns appear in Sara’s story and in what Breaking Barriers research heard repeatedly across STEMM pathways.</p>',
    },
    {
      id: 'meta-reflexivity',
      title: 'Meta-Reflexivity: Moving from Awareness to Action',
      content: '',
    },
    {
      id: 'meta-reflexive-manager',
      title: "Revisiting Sara's Story: The Meta-Reflexive Educator",
      content: '',
    },
    {
      id: 'actions',
      title: 'Small Actions, Big Shifts - Your Role in the System',
      content:
        '<h3>Small Actions, Big Shifts</h3><p>Use the at-a-glance table as a reference for noticing - not a checklist of instructions.</p>',
    },
    {
      id: 'educator-survey',
      title: 'Educator Module Survey',
      content: '',
    },
  ],
};

/** Educator Part 1 slide 1 - module overview video (same pattern as workplace Part 1). */
export const EDUCATOR_M1_OVERVIEW_YOUTUBE_VIDEO_ID = 'gM7CtAUv6lQ';

export const EDUCATOR_M1_SLIDES = [
  {
    title: 'How to Use this Module',
    youtubeVideoId: EDUCATOR_M1_OVERVIEW_YOUTUBE_VIDEO_ID,
  },
];

/** Educator Part 2 slide 1 - Sara case study video. */
export const EDUCATOR_M2_SARA_STORY_YOUTUBE_VIDEO_ID = 'QW946ycOdOk';

export const EDUCATOR_M2_SLIDES = [
  {
    title: 'Why This Story Matters',
    youtubeVideoId: EDUCATOR_M2_SARA_STORY_YOUTUBE_VIDEO_ID,
    lines: [
      'This module introduces the animated story of “Sara.” She doesn’t represent a single person, or an exceptional case. Her story reflects patterns that showed up repeatedly in our research - especially at moments where academic progress depends on endorsement, access, and informal guidance.',
      'As you watch, pay attention to the small moments:',
    ],
    bullets: [
      "Who explains (or doesn't explain) the unwritten rule.",
      'Who is left to interpret silence.',
      'The consequence',
    ],
    closingLine: "Let's begin.",
  },
  { title: 'Pause & Reflect', lines: [] },
  { title: 'Pause & Reflect', lines: [] },
  { title: 'Why this Matters (The Research Connection)', lines: [] },
];

export const EDUCATOR_M3_REFLECTION_SLIDES = [
  'As you sit with Sara’s story, notice your first reactions.',
  'Did anything feel familiar? Routine? Unremarkable?',
  'Did anything feel uncomfortable - or easy to rationalize?',
  'There is nothing to solve here. This meta-reflexive pause is simply to surface your instinctive interpretation. Often, our first reaction reveals the standards we assume are neutral: fairness, rigor, initiative, professionalism.',
  'Take a moment. What did you assume was happening?',
];

export const EDUCATOR_M3_RESEARCH_CONNECTION_SLIDE = {
  title: 'Why this Matters (The Research Connection)',
  subtitle: 'What we learned from our research',
  lines: [
    {
      html:
        'The patterns you saw in Sara’s story reflect what we heard repeatedly through the <em>Breaking Barriers</em> research: inequity in STEMM is primarily a <strong>conversion problem, not a capability problem.</strong> In other words, many South Asian women are excelling academically. What proves less reliable is the translation of educational achievement into opportunity - from research access, sponsorship, employment to advancement.',
    },
    {
      html:
        'In the video, Sara meets every formal requirement. Her GPA qualifies her. The application is open.',
    },
    {
      html:
        'The phrase “Evidence of supervisor support required” appears procedural. Neutral. Standard. But the pathway to securing that support is informal.',
    },
    {
      html:
        'Rather than asking, “<em>Why didn’t she follow up differently?</em>” we ask a different question:',
    },
    {
      html:
        '<strong>What mechanisms convert qualification into opportunity - and how are those mechanisms distributed?</strong>',
    },
  ],
  researchTitle: 'What We Learned: The Leak Is Patterned',
  researchLines: [
    {
      html:
        'Through the <em>Breaking Barriers</em> research, we observed that barriers intensify at specific transition points - particularly at the Bachelor’s level and during the university → work handoff.',
    },
    {
      html:
        'These moments share a common feature: Advancement depends less on coursework and more on endorsement.',
    },
    'Across the pathway:',
  ],
  researchBullets: [
    'Graduate admission depends on supervisor support.',
    'Employment depends on credible references.',
    'Advancement depends on nomination and visibility.',
  ],
};

/** Part 2 research-connection figure modal footer (leaky pipeline popup). */
export const EDUCATOR_PIPELINE_FIGURE_MODAL_FOOTER = [
  'When endorsement operates informally, conversion becomes uneven - even inside high-performing systems.',
  'These patterns are not random. They emerge consistently at points where authority determines who is "ready," "supported," or "recommended."',
  'This section names those structural conditions based on our research.',
];

export const EDUCATOR_M4_RESEARCH_INSIGHT = {
  title: 'Research Insight:',
  body:
    'Our research found that despite having a high degree of educational attainment (27.6% of South Asian women aged 25-64 held a bachelor’s degree or higher in STEMM, compared with 16.9% of all women, as per Statistics Canada, 2021); unemployment in our sample was 20.0%. Nearly three-quarters of those unemployed were early-career (under seven years since graduation), and unemployment was highest among Bachelor’s (25%) and Diploma (36%) holders.',
  quoteTitle: 'In Her Own Words',
  quote:
    '"Like all of this navigation past undergrad was entirely on my own. Figuring out how to apply to grad school was entirely on my own. Like navigating the bureaucracy of a PhD, which is so much and no one ever told me about. Like, all of that was, on my own."',
};

export const EDUCATOR_M4_GUIDED_STEPS = [
  'Review the five barriers in the table, then explore each numbered hotspot.',
  'On the next screen, optionally share which barriers feel most familiar in your context.',
  'Open “Research deep dive” in each barrier for additional framing and quotes.',
];

/** Sara story + everyday meaning + optional deep-dive copy */
export const EDUCATOR_FIVE_BARRIERS = [
  {
    name: 'Institutional Failure ',
    researchThemeTitle: 'Institutional Failure ',
    researchStats:
      '84% (42/50) of our participants discussed this, and 272 quotes were collected for the Institutional Failure theme/barrier (272/9265), representing 2.94% of all quotes.',
    researchChart: {
      participantPercent: 84,
      participantCount: 42,
      participantTotal: 50,
    },
    participantContext:
      'The inability or unwillingness of STEMM institutions to create equitable environments, often resulting in exclusion, burnout, or attrition.',
    participantQuotes: [
      'A lot of this stuff tends to be, like, tacit knowledge. Like, what are the right buttons to push, to advance in your career the way that you want? … My parents did not do advanced degrees. Like all of this navigation past undergrad was entirely on my own… Like navigating the bureaucracy of a PhD, which is so much and no one ever told me about… And it\'s kind of hard to advance when you don\'t have that knowledge in, like, your circle.'
    ],
    howItLooksInAnika:
      'The requirement is clear; the pathway to securing supervisor support is not.',
    everydayMeaning:
      'When expectations are unwritten, those with relational access move smoothly. Others interpret silence.',
    workplaceConditionTriggersIt:
      'Informal knowledge about who to contact, how often to follow up, and what counts as a “strong” inquiry.',
    leveragePoint: 'Make criteria and outreach pathways visible earlier in the process.',
  },
  {
    name: 'Gatekeeping in Mentorship & Opportunities',
    researchThemeTitle: 'Gatekeeping in Mentorship & Opportunities',
    researchStats:
      '70% (35/50) of our participants discussed this, and 133 quotes were collected for this theme/barrier (133/9265) 1.44%.',
    researchChart: {
      participantPercent: 70,
      participantCount: 35,
      participantTotal: 50,
    },
    participantContext:
      'The selective or exclusive nature of mentorship access in STEMM, where informal networks and biases often block women of color from key guidance or opportunities.',
    participantQuotes: [
      'And so the networking is important. So I just want to echo with "particant" that it\'s a kind of play, and it\'s a kind of game. But nobody here will teach you how to play the game.',
    ],
    howItLooksInAnika: 'Other applicants know who to approach. Support circulates through established relationships.',
    everydayMeaning: 'When access depends on proximity rather than transparent criteria, progression becomes uneven.',
    workplaceConditionTriggersIt:
      'Office-hour norms, lab culture, and who gets invited into “optional” conversations that shape trajectories.',
    leveragePoint: 'Reduce reliance on informal networks by creating accessible entry points.',
  },
  {
    name: 'Lack of Career Advocacy & Advancement Support',
    researchThemeTitle: 'Lack of Career Advocacy & Advancement Support',
    researchStats:
      '82% (41/50) of our participants discussed this, and 203 quotes were collected for this theme/barrier (203/9265) which is 2.19%.',
    researchChart: {
      participantPercent: 82,
      participantCount: 41,
      participantTotal: 50,
    },
    participantContext:
      'The absence of active career sponsorship, guidance, or championing, often hindering South Asian women\'s progression in STEMM environments.',
    participantQuotes: [
      'Like, I think most of the work like, it actually, sometimes I\'m like, wow, your experience or, like, how good your CV is, your cover letter. Sometimes it feels really futile and like it doesn\'t matter. Once you have someone to put you in contact with someone, you\'re suddenly seen as as credible and competent… But without those connections, it it\'s really, otherwise, I think, kind of difficult.',
    ],
    howItLooksInAnika: 'Sara sends careful emails but receives no response. No one actively endorses her application.',
    everydayMeaning:
      'Advancement depends on advocacy - introductions, nominations, timely backing - not just mentorship.',
    workplaceConditionTriggersIt:
      'Inboxes, reference norms, and committee practices that reward proactive advocacy unevenly.',
    leveragePoint: 'Build structured endorsement points (intro meetings, nomination prompts, proactive recommendations).',
  },
  {
    name: 'Barriers Across the STEMM Pathway',
    researchThemeTitle: 'Barriers Across the STEMM Pathway',
    researchStats:
      '76% (38/50) of our participants discussed this and 272 quotes were collected for this theme/barrier (272/9265) - 2.94%',
    researchChart: {
      participantPercent: 76,
      participantCount: 38,
      participantTotal: 50,
    },
    participantContext:
      'Obstacles at each educational milestone (e.g., high school, undergrad, graduate school) that disproportionately hinder South Asian women from progressing in STEMM.',
    participantQuotes: [
      'Almost every time there was an excuse for me not to be seen. Oh, she\'s too academic. Oh, she\'s not this. She\'s too much of that. She\'s too little of. Speak up. You\'re too loud. I got a lot of that.',
    ],
    howItLooksInAnika: 'The deadline passes. A qualified candidate stalls at the moment of conversion.',
    everydayMeaning:
      'The Bachelor’s → graduate school transition is a pressure ridge. Achievement alone does not guarantee mobility.',
    workplaceConditionTriggersIt:
      'Deadlines, funding cycles, and competitive pools that amplify small differences in visibility.',
    leveragePoint: 'Identify one transition point and clarify expectations at that stage.',
  },
  {
    name: 'Influence of Key System Actors (Teachers, Parents, Institutions)',
    researchThemeTitle: 'Influence of Key System Actors (Teachers, Parents, Institutions)',
    researchStats:
      '74% (37/50) of our participants discussed this and 299 quotes were collected for this theme/barrier (299/9265) - 3.23%',
    participantContext:
      'The role influential stakeholders play in shaping either enabling or constraining pathways for South Asian women pursuing STEMM careers.',
    participantQuotes: [
      'I visited at least six, six, employment counselors in different organizations, but unfortunately, they didn\'t even recognize my CV, my resume… we don\'t know, and the right information, so the where should we go, how should we get the support… if the STEM field has a really great mentorship… they can get the right track… But, I think that is a really, really lack of… still, it\'s not improved.',
    ],
    howItLooksInAnika: 'Endorsement moves through relationships rather than process.',
    everydayMeaning:
      'Decisions that appear administrative often carry discretionary power - shaping who is considered “ready.”',
    workplaceConditionTriggersIt:
      'Committee judgment, supervisor selection, and who is interpreted as “a good fit.”',
    leveragePoint: 'Examine where your discretion operates - and whether it is consistently applied.',
  },
];

/** Part 3 slide 1: preview beside “Click to explore” (educator-specific art). */
export const EDUCATOR_BARRIERS_INTRO_PREVIEW_IMAGE = 'images/e_1.jpg';

/** Single-scene layout: five hotspots on educator barrier model art. */
export const EDUCATOR_BARRIER_CAROUSEL_SLIDES = [
  {
    imageSrc: 'images/e_b1.jpg',
    alt: 'Illustration of systemic barriers - click numbered hotspots to read more',
    hotspots: [
      { barrierIndex: 0, left: 18, top: 32, label: '1' },
      { barrierIndex: 1, left: 42, top: 10, label: '2' },
      { barrierIndex: 2, left: 68, top: 36, label: '3' },
      { barrierIndex: 3, left: 30, top: 62, label: '4' },
      { barrierIndex: 4, left: 58, top: 68, label: '5' },
    ],
  },
];

export const EDUCATOR_BARRIER_HOTSPOTS = EDUCATOR_BARRIER_CAROUSEL_SLIDES.flatMap((slide) => slide.hotspots);

export const EDUCATOR_BARRIER_POLL_OPTIONS = [
  { value: 'institutional-failure', label: 'Institutional failure' },
  { value: 'sponsorship-scarcity', label: 'Lack of Career Advocacy & Advancement Support' },
  { value: 'informal-gatekeeping', label: 'Gatekeeping in Mentorship & Opportunities' },
  { value: 'transition-fragility', label: 'Barriers Across the STEMM Pathway' },
  { value: 'discretion-authority', label: 'Discretion at the point of authority' },
];

export const EDUCATOR_M5_HEADER = 'Meta-Reflexivity: Moving from Awareness to Action';

/** Part 4 slide 1: copy beside the Lottie animation and PDF download. */
export const EDUCATOR_M5_SLIDE0_SIDE = {
  intro:
    'There are no right answers. All of us carry internal stories that result in decisions that are small and ordinary. Situations from your life could look like:',
  situationExamples: [
    'recommending a student',
    'selecting for a lab or TA role',
    'encouraging someone to apply based on interpreted “readiness”',
  ],
  frameworkIntro:
    'These actions rarely feel dramatic. Yet, they accumulate into patterns. The 4-part framework below invites you to surface:',
  frameworkItems: [
    'what happened,',
    'what assumptions shaped interpretation,',
    'where discretion operates,',
    'and where redesign may be possible.',
  ],
};

export const EDUCATOR_M5_SLIDES = [
  {
    title: 'Meta-Reflexivity Framework',
    lines: [],
  },
  {
    title: 'Why this matters',
    lines: [
      'Meta-reflexivity goes a step beyond noticing how a story makes us feel. It invites a different kind of question: What shaped the way I\'m interpreting this - and where did those expectations come from?',
      'There are no right answers. All of us carry internal stories that result in decisions that are small and ordinary. Situations from your life could look like:',
    ],
    bullets: [
      'recommending a student',
      'selecting for a lab or TA role',
      'encouraging someone to apply based on interpreted “readiness”',
    ],
    closingLine:
      'These actions rarely feel dramatic. Yet, they accumulate into patterns. The 4-part framework below invites you to surface:',
    closingBullets: [
      'what happened,',
      'what assumptions shaped interpretation,',
      'where discretion operates,',
      'and where redesign may be possible.',
    ],
  },
];

/** Educator Part 5 slide 1 - revisiting Sara story video. */
export const EDUCATOR_M6_REVISITING_SARA_YOUTUBE_VIDEO_ID = 'HH8cFaRuSuY';

export const EDUCATOR_M6_SLIDES = [
  {
    title: 'Revisiting Sara’s Story: The Meta-Reflexive Educator',
    youtubeVideoId: EDUCATOR_M6_REVISITING_SARA_YOUTUBE_VIDEO_ID,
    videoLayout: true,
    lines: [
      {
        html:
          'This section illustrates the practical application of the Meta-Reflexivity framework to Sara’s story, demonstrating how a leader can move from noticing a pattern to <strong>intentionally shifting the conditions around it.</strong>',
      },
      {
        html:
          'In Sara’s original story, no one explicitly blocked her. The requirement was standard and the process appeared neutral. Yet endorsement moved informally, and the absence of response became decisive.',
      },
      {
        html:
          'A meta-reflexive response doesn’t require dramatic intervention. It begins with <strong>noticing</strong>, then <strong>questioning</strong>, then <strong>acting differently in small, concrete ways.</strong>',
      },
    ],
  },
  {
    title: 'The Internal Shift',
    lines: [],
    stages: [
      {
        label: 'The Observation',
        text: 'An educator notices that qualified students often stall at the supervisor-support stage - particularly those without prior relational access.',
      },
      {
        label: 'The Assumption',
        text: 'They catch an internal standard: “Strong candidates will already know how to secure support.”',
      },
      {
        label: 'The Origin',
        text: 'Using meta-reflexivity, they pause and ask: Where did I learn that readiness includes insider navigation? They recognize that this expectation reflects inherited academic norms - not necessarily student capability.',
      },
      {
        label: 'The Shift',
        text: 'Instead of treating informal access as inevitable, they make the pathway more visible.',
        listIntro: 'They choose to:',
        bullets: [
          'Clarify how supervisor support is typically secured',
          'Offer structured guidance rather than waiting for informal outreach',
          'Respond within a defined timeframe',
          'Proactively encourage strong students to seek endorsement',
        ],
      },
    ],
    closingLines: [
      'The admissions criteria do not change. The standard does not lower. But the process becomes more reliable.',
      'The system does not transform overnight. Yet the conditions begin to shift to allow for more equitable outcomes, as we will see in the alternate ending of the story where endorsement is no longer dependent on cold-email luck.',
    ],
  },
];

/** Educator Part 6 - hotspots first, overview video second, commitment third. */
export const EDUCATOR_M7_SLIDE0_YOUTUBE_VIDEO_ID = 'wL4nNc2nRK8';

/** Module 7 (Small Actions): three-slide flow (hotspots + video + commitment). */
export const EDUCATOR_M7_SLIDES = [
  {
    title: 'At-a-Glance: Barriers and Educator Leverage Points',
    lines: ['Click the numbered spots on the image to explore.'],
  },
  {
    title: 'Small Actions, Big Shifts - Your Role in the System',
    youtubeVideoId: EDUCATOR_M7_SLIDE0_YOUTUBE_VIDEO_ID,
    lines: [],
  },
  {
    title: 'One Intentional Shift (Optional)',
    lines: [
      'This is not about overhauling a system - just a place to name one thing you\'re willing to examine.',
      'One way I commit to pausing, questioning, or adjusting how endorsement or access operates is:',
      'If you would like to see how others have responded to this question, feel free to anonymously enter your response into the LMS prompt.',
    ],
  },
];

export const EDUCATOR_ACTIONS_IMAGE_SRC = 'images/e_b2.jpg';

export const EDUCATOR_ACTIONS_HOTSPOTS = [
  { spotIndex: 0, left: 14, top: 28, label: '1' },
  { spotIndex: 1, left: 38, top: 20, label: '2' },
  { spotIndex: 2, left: 86, top: 30, label: '3' },
  { spotIndex: 3, left: 16, top: 74, label: '4' },
  { spotIndex: 4, left: 62, top: 84, label: '5' },
];

export const EDUCATOR_ACTIONS_TABLE_CONTENT = [
  {
    name: 'Institutional Failure ',
    howItLooksInAnika: 'The requirement is clear; the pathway to securing support is not.',
    workplaceConditionTriggersIt: 'When expectations are unwritten, relational access determines who moves smoothly.',
    leveragePoint: 'Make criteria and outreach pathways visible earlier in the process.',
  },
  {
    name: 'Lack of Career Advocacy & Advancement Support',
    howItLooksInAnika: 'Sara sends careful emails but receives no response; no one endorses her file.',
    workplaceConditionTriggersIt: 'Advancement depends on advocacy, not only mentorship.',
    leveragePoint: 'Build structured endorsement points (intro meetings, nomination prompts, proactive recommendations).',
  },
  {
    name: 'Gatekeeping in Mentorship & Opportunities',
    howItLooksInAnika: 'Other applicants know who to approach; support circulates through established relationships.',
    workplaceConditionTriggersIt: 'Access depends on proximity as much as criteria.',
    leveragePoint: 'Reduce reliance on informal networks by creating accessible entry points.',
  },
  {
    name: 'Barriers Across the STEMM Pathway',
    howItLooksInAnika: 'A qualified candidate stalls at the moment of conversion.',
    workplaceConditionTriggersIt: 'Bachelor’s → graduate and university → work are pressure ridges.',
    leveragePoint: 'Pick one transition and clarify expectations at that stage.',
  },
  {
    name: 'Influence of Key System Actors (Teachers, Parents, Institutions)',
    howItLooksInAnika: 'Endorsement moves through relationships rather than process.',
    workplaceConditionTriggersIt: 'Administrative language can hide discretionary power.',
    leveragePoint: 'Examine where your discretion operates - and whether it is consistently applied.',
  },
];

function trimReactEnv(name) {
  const v = process.env[name];
  if (v == null) return '';
  return String(v).trim();
}

export const SARA_CASE_STUDY_VIDEO = {
  localSrc: 'videos/sara-case-study.mp4',
  remoteSrc: trimReactEnv('REACT_APP_SARA_CASE_STUDY_VIDEO_URL'),
  poster: trimReactEnv('REACT_APP_SARA_CASE_STUDY_VIDEO_POSTER') || 'images/thumb1.jpg',
  title: 'Almost there - Sara’s story',
};

export const REVISITING_SARA_STORY_VIDEO = {
  localSrc: 'videos/revisiting-sara-story.mp4',
  remoteSrc: trimReactEnv('REACT_APP_REVISITING_SARA_VIDEO_URL'),
  poster: trimReactEnv('REACT_APP_REVISITING_SARA_VIDEO_POSTER') || 'images/thumb2.jpg',
  title: 'Revisiting Sara’s Story: The Meta-Reflexive Educator',
};

export const EDUCATOR_META_PRIVATE_NOTE_KEY = 'lms-educator-meta-private-note';
